import { BaseClientEntities, filterOutEntities, PartialBaseEntities } from '@wix/add-panel-common';
import type { ConductionResults } from '@wix/wix-petri-client';
import { HttpClient } from '@wix/http-client';

const DEFAULT_LOCALE = 'en';

const ADD_PANEL_SCOPE = 'add-panel-data';

const httpClient = new HttpClient();

export interface DataFetcher {
  loadEntities(data: { experiments: ConductionResults }): Promise<{ entities: BaseClientEntities; version: string }>;
}

const memoOnce = <T>(fn: () => T) => {
  let memoized: T;
  let called = false;
  return () => {
    if (!called) {
      called = true;
      memoized = fn();
    }
    return memoized;
  };
};

export class StaticsFetcher implements DataFetcher {
  constructor(private readonly opts: { entitiesStaticsUrl: string; locale: string | undefined }) {}
  private buildStaticsUrl(locale: string) {
    return `${this.opts.entitiesStaticsUrl}/assets/data-${locale}.json`;
  }

  private fetchStatics = memoOnce(async (): Promise<{ entities: PartialBaseEntities }> => {
    const { locale = DEFAULT_LOCALE } = this.opts;

    const resp = await httpClient
      .get(this.buildStaticsUrl(locale))
      .catch(() => httpClient.get(this.buildStaticsUrl(DEFAULT_LOCALE)));

    return resp.data;
  });

  private fetchExperiments = memoOnce(async () => {
    const resp = await httpClient.get<ConductionResults>(`/_api/wix-laboratory-server/laboratory/conductAllInScope`, {
      params: { scope: ADD_PANEL_SCOPE },
    });
    return resp.data;
  });

  async loadEntities(data: {
    experiments: ConductionResults;
  }): Promise<{ entities: BaseClientEntities; version: string }> {
    const { entitiesStaticsUrl, locale = DEFAULT_LOCALE } = this.opts;

    const version = entitiesStaticsUrl.split('/').pop()!;

    const [staticsData, addPanelExperiments] = await Promise.all([this.fetchStatics(), this.fetchExperiments()]);

    const experiments = { ...addPanelExperiments, ...data.experiments };
    const entities = filterOutEntities(staticsData.entities, { experiments, locale });

    return { version, entities };
  }
}
